import { floorAtPosition } from "../math-utils/math-utils.js";
const MIN_VALID_YEAR = 1;
const MAX_VALID_YEAR = 9999;
function isLeapYear(year) {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
}
function isValidYear(year) {
  return year >= MIN_VALID_YEAR && year <= MAX_VALID_YEAR && Number.isInteger(year);
}
function isValidMonth(month) {
  return month >= 1 && month <= 12 && Number.isInteger(month);
}
function isValidDay(day) {
  return day > 0 && day < 32 && Number.isInteger(day);
}
function validateMonthDay(month, day) {
  let isValid = true;
  let reason = [];
  if (!isValidMonth(month)) {
    isValid = false;
    reason.push("month");
  }
  if (!isValidDay(day)) {
    isValid = false;
    reason.push("day");
  }
  if (isValid) {
    if (month === 2) {
      if (day > 29) {
        isValid = false;
        reason.push("month-day");
      }
    } else if (month === 4 || month === 6 || month === 9 || month === 11) {
      if (day > 30) {
        isValid = false;
        reason.push("month-day");
      }
    }
  }
  return {
    isValid,
    reasons: reason
  };
}
function validateYearMonthDay(year, month, day) {
  let isValid = true;
  let reason = [];
  if (!isValidYear(year)) {
    isValid = false;
    reason.push("year");
  }
  const validMonthDay = validateMonthDay(month, day);
  if (!validMonthDay.isValid) {
    return validMonthDay;
  }
  if (month === 2) {
    if (isLeapYear(year) && day > 29) {
      isValid = false;
      reason.push("year-month-day");
    }
    if (!isLeapYear(year) && day > 28) {
      isValid = false;
      reason.push("year-month-day");
    }
  }
  return {
    isValid,
    reasons: reason
  };
}
function partiallyValidateYearMonthDay(year, month, day) {
  if (Number.isNaN(year) && Number.isNaN(month) && Number.isNaN(day)) {
    return { isValid: true, reasons: [] };
  }
  if (Number.isNaN(year) && Number.isNaN(month)) {
    if (isValidDay(day)) {
      return { isValid: true, reasons: [] };
    } else {
      return { isValid: false, reasons: ["day"] };
    }
  }
  if (Number.isNaN(year) && Number.isNaN(day)) {
    if (isValidMonth(month)) {
      return { isValid: true, reasons: [] };
    } else {
      return { isValid: false, reasons: ["month"] };
    }
  }
  if (Number.isNaN(month) && Number.isNaN(day)) {
    if (isValidYear(year)) {
      return { isValid: true, reasons: [] };
    } else {
      return { isValid: false, reasons: ["year"] };
    }
  }
  if (Number.isNaN(year)) {
    return validateMonthDay(month, day);
  }
  if (Number.isNaN(day)) {
    let isValid = true;
    let reason = [];
    if (!isValidYear(year)) {
      isValid = false;
      reason.push("year");
    }
    if (!isValidMonth(month)) {
      isValid = false;
      reason.push("month");
    }
    return {
      isValid,
      reasons: reason
    };
  }
  if (Number.isNaN(month)) {
    let isValid = true;
    let reason = [];
    if (!isValidYear(year)) {
      isValid = false;
      reason.push("year");
    }
    if (!isValidDay(day)) {
      isValid = false;
      reason.push("day");
    }
    return {
      isValid,
      reasons: reason
    };
  }
  return validateYearMonthDay(year, month, day);
}
function getTwoDigitYearFuture(year, fromDate) {
  if (year > 99 || year < 0) {
    return NaN;
  }
  const date = fromDate ?? /* @__PURE__ */ new Date();
  const baseYear = floorAtPosition(date.getFullYear(), 2);
  if (baseYear + year > date.getFullYear()) {
    return baseYear + year;
  }
  return baseYear + 100 + year;
}
function getTwoDigitYearPast(year, fromDate) {
  if (year > 99 || year < 0) {
    return NaN;
  }
  const date = fromDate ?? /* @__PURE__ */ new Date();
  const baseYear = floorAtPosition(date.getFullYear(), 2);
  if (baseYear + year < date.getFullYear()) {
    return baseYear + year;
  }
  return baseYear - 100 + year;
}
const isValidDate = (date) => !!date && !isNaN(date.getTime());
const greaterThan = (a, b) => a > b;
const greaterThanOrEqual = (a, b) => a >= b;
const lessThan = (a, b) => a < b;
const lessThanOrEqual = (a, b) => a <= b;
function compareDays(dateA, dateB, yearCompare, monthCompare, dayCompare) {
  if (!dateA || !dateB) return false;
  if (yearCompare(dateA.getUTCFullYear(), dateB.getUTCFullYear())) return true;
  if (dateA.getUTCFullYear() === dateB.getUTCFullYear() && monthCompare(dateA.getUTCMonth(), dateB.getUTCMonth()))
    return true;
  if (dateA.getUTCFullYear() === dateB.getUTCFullYear() && dateA.getUTCMonth() === dateB.getUTCMonth() && dayCompare(dateA.getUTCDate(), dateB.getUTCDate()))
    return true;
  return false;
}
function compareMonths(dateA, dateB, yearCompare, monthCompare) {
  if (!dateA || !dateB) return false;
  if (yearCompare(dateA.getUTCFullYear(), dateB.getUTCFullYear())) return true;
  if (dateA.getUTCFullYear() === dateB.getUTCFullYear() && monthCompare(dateA.getUTCMonth(), dateB.getUTCMonth()))
    return true;
  return false;
}
const isDayGreaterThan = (dateA, dateB) => compareDays(dateA, dateB, greaterThan, greaterThan, greaterThan);
const isDayGreaterThanOrEqual = (dateA, dateB) => compareDays(dateA, dateB, greaterThan, greaterThan, greaterThanOrEqual);
const isDayLessThan = (dateA, dateB) => compareDays(dateA, dateB, lessThan, lessThan, lessThan);
const isDayLessThanOrEqual = (dateA, dateB) => compareDays(dateA, dateB, lessThan, lessThan, lessThanOrEqual);
const isDayInRange = (date, start, end) => !isDayLessThan(end, start) && isDayGreaterThanOrEqual(date, start) && isDayLessThanOrEqual(date, end);
const isMonthGreaterThan = (dateA, dateB) => compareMonths(dateA, dateB, greaterThan, greaterThan);
const isMonthGreaterThanOrEqual = (dateA, dateB) => compareMonths(dateA, dateB, greaterThan, greaterThanOrEqual);
const isMonthLessThan = (dateA, dateB) => compareMonths(dateA, dateB, lessThan, lessThan);
const isMonthLessThanOrEqual = (dateA, dateB) => compareMonths(dateA, dateB, lessThan, lessThanOrEqual);
function isYearEqual(dateA, dateB) {
  if (!dateA || !dateB) return false;
  return dateA.getUTCFullYear() === dateB.getUTCFullYear();
}
function isMonthEqual(dateA, dateB) {
  if (!dateA || !dateB) return false;
  return dateA.getUTCFullYear() === dateB.getUTCFullYear() && dateA.getUTCMonth() === dateB.getUTCMonth();
}
function isDayEqual(dateA, dateB) {
  if (!dateA || !dateB) return false;
  return dateA.getUTCFullYear() === dateB.getUTCFullYear() && dateA.getUTCMonth() === dateB.getUTCMonth() && dateA.getUTCDate() === dateB.getUTCDate();
}
const maxDate = (dateA, dateB) => {
  if (!dateA || !dateB) return null;
  return dateB.getTime() > dateA.getTime() ? dateB : dateA;
};
const minDate = (dateA, dateB) => {
  if (!dateA || !dateB) return null;
  return dateB.getTime() < dateA.getTime() ? dateB : dateA;
};
const getNumMonthsBetween = (dateA, dateB) => (dateB.getUTCFullYear() - dateA.getFullYear()) * 12 + (dateB.getUTCMonth() - dateA.getUTCMonth());
const compareDayRange = (date, start, end, startCompare, endCompare) => startCompare(date, start) && endCompare(date, end);
const isDayInRangeInclusive = (date, start, end) => compareDayRange(
  date,
  start,
  end,
  isDayGreaterThanOrEqual,
  isDayLessThanOrEqual
);
const isDayInRangeExclusive = (date, start, end) => compareDayRange(date, start, end, isDayGreaterThan, isDayLessThan);
const isDayInRangeStartExclusive = (date, start, end) => compareDayRange(date, start, end, isDayGreaterThan, isDayLessThanOrEqual);
const isDayInRangeEndExclusive = (date, start, end) => compareDayRange(date, start, end, isDayGreaterThanOrEqual, isDayLessThan);
const getDaysInRangeExclusive = (start, end) => {
  const startTime = start?.getTime() ?? 0;
  const endTime = end?.getTime() ?? 0;
  return Math.floor((endTime - startTime) / 864e5);
};
const getDaysInRangeInclusive = (start, end) => {
  const startTime = start?.getTime() ?? 0;
  const endTime = end?.getTime() ?? 0;
  return Math.floor((endTime - startTime) / 864e5) + 1;
};
function setTimeToZero(date) {
  if (!date) return date;
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
}
function currentDate() {
  return setTimeToZero(/* @__PURE__ */ new Date());
}
const currentTime = () => /* @__PURE__ */ new Date();
function currentDateToLocalDateFormat(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
export {
  MAX_VALID_YEAR,
  MIN_VALID_YEAR,
  currentDate,
  currentDateToLocalDateFormat,
  currentTime,
  getDaysInRangeExclusive,
  getDaysInRangeInclusive,
  getNumMonthsBetween,
  getTwoDigitYearFuture,
  getTwoDigitYearPast,
  isDayEqual,
  isDayGreaterThan,
  isDayGreaterThanOrEqual,
  isDayInRange,
  isDayInRangeEndExclusive,
  isDayInRangeExclusive,
  isDayInRangeInclusive,
  isDayInRangeStartExclusive,
  isDayLessThan,
  isDayLessThanOrEqual,
  isLeapYear,
  isMonthEqual,
  isMonthGreaterThan,
  isMonthGreaterThanOrEqual,
  isMonthLessThan,
  isMonthLessThanOrEqual,
  isValidDate,
  isValidDay,
  isValidMonth,
  isValidYear,
  isYearEqual,
  maxDate,
  minDate,
  partiallyValidateYearMonthDay,
  setTimeToZero,
  validateMonthDay,
  validateYearMonthDay
};
